import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import React from 'react';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { DataElementId } from '../../../constants/data-element-tracking-ids';
import {
  Button,
  ButtonVariant,
} from '../../../component_library/components/Button';
import Tooltip from '../../../component_library/components/Tooltip';
import Select from '../../../component_library/components/Select';
import HtmlBox from '../../../component_library/components/HtmlBox';
import type { IDialogData } from '../../../dotcms/dotcms-models/dotcms-dialog';
import Dialog from '../../../component_library/components/Dialog';
import { LoginMethod } from '../../../selectors/interfaces';
import type { HtmlFromDotcms } from '../../../dotcms/dotcms-models/converters-from-dotcms';
import AttentionBox from '../../../component_library/components/AttentionBox';
import { SecurityDisclaimer } from './security-disclaimer';
import { LoginDobFormDate } from './login-dob-form-date';
import { LoginDobFormEmployeeId } from './login-dob-form-employee-id';
import { LoadingOverlay } from '../../../components/abc/loading-overlay/loading-overlay';
import Typography, {
  TypographyVariant,
} from '../../../component_library/components/Typography';
import useLoginDobForm from './hooks/use-login-dob-form';
import { LoginFormBox } from './login-sso-form';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';

interface IProps {
  loginMethod: LoginMethod;
}

export const LoginDobForm: React.FC<IProps> = ({ loginMethod }) => {
  const {
    theme,
    muiTheme,
    isMobile,
    isTabletMini,
    data,
    loading,
    error,
    dob,
    setDob,
    setDobValidationError,
    dobError,
    organization,
    setOrganization,
    organizationOptions,
    organizationError,
    employeeId,
    setEmployeeId,
    employeeIdError,
    disableSubmit,
    validationErrors,
    showValidationErrors,
    handleLoginSubmit,
    encryptedDataDisclaimerText,
    sendRequestLoading,
    showUnableToVerifyPopup,
    closeUnableToVerifyPopup,
    showUnableToEligibilityPopup,
    closeUnableToEligibilityPopup,
    showAlreadyEnrolledPopup,
    closeAlreadyEnrolledPopup,
    showGenericError,
  } = useLoginDobForm(loginMethod);
  const themeToken = useThemeToken();

  if (loading) {
    return <LoadingOverlay />;
  }

  if (error || !data) {
    return null;
  }

  const renderLabelWithTooltip = (
    label: HtmlFromDotcms,
    tooltipProps: IPropsWithTestId & {
      infoContent: ReactNode;
    },
  ) => (
    <Box sx={{ display: 'inline-flex' }}>
      <HtmlBox html={label} />
      <Tooltip
        iconAfterWord
        isAnchored
        theme={theme}
        dataElementId={tooltipProps.dataElementId}
        infoContent={tooltipProps.infoContent}
      />
    </Box>
  );

  const renderEmployeeIdTooltip = () => {
    const overridedEmployeeIdFieldTooltip = organization
      ? data.employeeIdFieldTooltipOverride.find(
        (x) => x.key.toLowerCase() === organization.toLowerCase(),
      )?.value
      : undefined;
    return (
      overridedEmployeeIdFieldTooltip || (
        <HtmlBox html={data.employeeIdFieldTooltip} />
      )
    );
  };

  const renderDialog = (
    id: string,
    dialog: IDialogData | undefined,
    dialogElementId: IPropsWithTestId['dataElementId'],
    buttonDataElementId: IPropsWithTestId['dataElementId'],
    isOpen: boolean,
    handleDialogClose: () => void,
  ) =>
    dialog &&
    isOpen && (
      <Dialog
        dataElementId={dialogElementId}
        id={id}
        open={isOpen}
        theme={theme}
        muiTheme={muiTheme}
        centeredContent
        headlineContent={<HtmlBox html={dialog.headline} />}
        primaryButton={
          dialog.primaryButton
            ? {
              content: dialog.primaryButton.text,
              onClick: handleDialogClose,
              dataElementId: buttonDataElementId,
              variant: ButtonVariant.SECONDARY,
            }
            : undefined
        }
        onDialogClose={handleDialogClose}
        additionalStyles={{
          a: { color: theme.colors.secondary, fontWeight: 700 },
          button: { border: 'none' },
        }}
      >
        <HtmlBox html={dialog.content} />
      </Dialog>
    );

  return (
    <LoginFormBox
      colors={theme.colors}
      isWithBorders={isMobile || isTabletMini}
      dataElementId={DataElementId.LOGIN_PAGE_DOB_FORM}
    >
      <Box
        alignItems="center"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '48px',
        }}
      >
        <Typography
          variant={TypographyVariant.H4}
          theme={theme}
          additionalStyles={{
            textAlign: 'center',
          }}
        >
          <HtmlBox html={data.formHeadline} />
        </Typography>
        {showGenericError && (
          <AttentionBox
            dataElementId={DataElementId.LOGIN_PAGE_DOB_FORM_ERROR}
            description={<HtmlBox html={data.somethingWrongText} />}
            variant="error"
            theme={theme}
            muiTheme={muiTheme}
            additionalStyles={{ a: { color: 'inherit' } }}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '16px',
            }}
          >
            <LoginDobFormDate
              dataElementId={DataElementId.LOGIN_PAGE_DOB_DATE_PICKER}
              dob={dob}
              setDob={setDob}
              setDobValidationError={setDobValidationError}
              showError={showValidationErrors && !!dobError}
              data={data}
              theme={theme}
            />
            {loginMethod === LoginMethod.BUSINESS_GROUP && (
              <>
                <Select
                  id="login-organization"
                  dataElementId={
                    DataElementId.LOGIN_PAGE_DOB_ORGANIZATION_SELECT
                  }
                  label={<HtmlBox html={data.organizationFieldName} />}
                  value={organization}
                  onChange={setOrganization}
                  options={organizationOptions}
                  placeholderText={data.organizationFieldPlaceholder}
                  customError={
                    showValidationErrors && organizationError ? (
                      <HtmlBox html={data.organizationFieldError} />
                    ) : undefined
                  }
                  fullWidth
                  theme={theme}
                />
                <LoginDobFormEmployeeId
                  dataElementId={DataElementId.LOGIN_PAGE_DBO_EMPLOYEE_ID_INPUT}
                  employeeId={employeeId}
                  setEmployeeId={setEmployeeId}
                  showError={showValidationErrors && employeeIdError}
                  label={renderLabelWithTooltip(data.employeeIdFieldName, {
                    dataElementId:
                      DataElementId.LOGIN_PAGE_DBO_EMPLOYEE_ID_TOOLTIP,
                    infoContent: renderEmployeeIdTooltip(),
                  })}
                  data={data}
                  theme={theme}
                />
              </>
            )}
          </Box>
          <Button
            type="submit"
            onClick={handleLoginSubmit}
            dataElementId={DataElementId.LOGIN_PAGE_DBO_LOGIN_BUTTON}
            variant={ButtonVariant.PRIMARY}
            loading={sendRequestLoading}
            isDisabled={
              disableSubmit || (!!validationErrors && showValidationErrors)
            }
            themeToken={themeToken}
            additionalStyles={{ marginTop: '24px', justifyContent: 'center' }}
          >
            <Typography
              variant={TypographyVariant.MEDIUM_BODY_BOLD}
              theme={theme}
            >
              <HtmlBox html={data.submitButtonText} />
            </Typography>
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <SecurityDisclaimer
              securityNote={encryptedDataDisclaimerText}
              theme={theme}
            />
          </Box>
          {renderDialog(
            'login-dob-unable-to-verify-popup',
            data.unableToVerifyPopup,
            DataElementId.LOGIN_PAGE_DBO_UNABLE_TO_VERIFY_POPUP,
            DataElementId.LOGIN_PAGE_DBO_UNABLE_TO_VERIFY_CLOSE_BUTTON,
            showUnableToVerifyPopup,
            closeUnableToVerifyPopup,
          )}
          {renderDialog(
            'login-dob-unable-to-eligibility-popup',
            data.unableToEligibilityPopup,
            DataElementId.LOGIN_PAGE_DBO_UNABLE_TO_ELIGIBILITY_POPUP,
            DataElementId.LOGIN_PAGE_DBO_UNABLE_TO_ELIGIBILITY_CLOSE_BUTTON,
            showUnableToEligibilityPopup,
            closeUnableToEligibilityPopup,
          )}
          {renderDialog(
            'login-dob-already-enrolled-popup',
            data.alreadyEnrolledPopup,
            DataElementId.LOGIN_PAGE_DBO_ALREADY_ENROLLED_POPUP,
            DataElementId.LOGIN_PAGE_DBO_ALREADY_ENROLLED_CLOSE_BUTTON,
            showAlreadyEnrolledPopup,
            closeAlreadyEnrolledPopup,
          )}
        </Box>
      </Box>
    </LoginFormBox>
  );
};
